/**
 * Generated by scripts/generate-drizzle-schema.mjs. Do not edit by hand.
 * This product's inputs: scripts/drizzle-schema.config.mjs.
 * Regenerate: bash scripts/client-schema-fresh.sh (data-model-change skill).
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

export const attachment = sqliteTable(
  "attachment",
  {
    id: text("id").primaryKey(),
    todo_id: text("todo_id"),
    filename: text("filename"),
    content_type: text("content_type"),
    size: integer("size"),
    url: text("url"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    list_id: text("list_id"),
    user_id: text("user_id"),
    is_shared: integer("is_shared"),
    hash: text("hash"),
  },
  (table) => [
    /** WHERE user_id = ? in getHasLocalDataQuery */
    index("attachment_by_user").on(table.user_id),
  ],
);

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

// Small dataset (dozens of habits) but queried on every Today render.
export const habit = sqliteTable(
  "habit",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    icon: text("icon"),
    color: text("color"),
    schedule_kind: text("schedule_kind"),
    weekdays: text("weekdays"),
    times_per_week: integer("times_per_week"),
    sort_order: integer("sort_order"),
    archived: integer("archived"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    goal_kind: text("goal_kind"),
    target_amount: integer("target_amount"),
    target_unit: text("target_unit"),
    deleted_at: text("deleted_at"),
    current_version_id: text("current_version_id"),
  },
  (table) => [
    /** WHERE user_id = ? AND archived = 0 ORDER BY sort_order in getHabitsQuery */
    index("habit_by_user").on(table.user_id, table.archived, table.sort_order),
  ],
);

// Largest table in the domain (1 row per habit and day). Critical indexes.
export const habit_completion = sqliteTable(
  "habit_completion",
  {
    id: text("id").primaryKey(),
    habit_id: text("habit_id"),
    user_id: text("user_id"),
    date: text("date"),
    status: text("status"),
    note: text("note"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    amount: integer("amount"),
  },
  (table) => [
    /** WHERE user_id = ? AND date BETWEEN ? AND ? in getCompletionsRangeQuery */
    index("habit_completion_by_user_date").on(table.user_id, table.date),
    /** WHERE habit_id = ? ORDER BY date DESC in getHabitCompletionsQuery (streaks) */
    index("habit_completion_by_habit_date").on(table.habit_id, table.date),
  ],
);

export const habit_reminder = sqliteTable(
  "habit_reminder",
  {
    id: text("id").primaryKey(),
    habit_id: text("habit_id"),
    user_id: text("user_id"),
    time_of_day: text("time_of_day"),
    enabled: integer("enabled"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? in getRemindersQuery */
    index("habit_reminder_by_user").on(table.user_id),
    /** WHERE habit_id = ? in getHabitRemindersQuery */
    index("habit_reminder_by_habit").on(table.habit_id),
  ],
);

export const habit_version = sqliteTable(
  "habit_version",
  {
    id: text("id").primaryKey(),
    habit_id: text("habit_id"),
    user_id: text("user_id"),
    version_number: integer("version_number"),
    name: text("name"),
    schedule_kind: text("schedule_kind"),
    weekdays: text("weekdays"),
    times_per_week: integer("times_per_week"),
    goal_kind: text("goal_kind"),
    target_amount: integer("target_amount"),
    target_unit: text("target_unit"),
    text: text("text"),
    client_ts: text("client_ts"),
    server_ts: text("server_ts"),
  },
  (table) => [
    /** WHERE habit_id = ? ORDER BY server_ts in getHabitVersionsQuery */
    index("habit_version_by_habit").on(table.habit_id, table.server_ts),
    /** WHERE user_id = ? in getUserHabitVersionsQuery (user timeline) */
    index("habit_version_by_user").on(table.user_id),
  ],
);

// Small dataset per user — indexes not needed currently.
export const member = sqliteTable("member", {
  id: text("id").primaryKey(),
  organization_id: text("organization_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
});

// Small dataset — indexes not needed currently.
export const organization = sqliteTable("organization", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  logo: text("logo"),
  metadata: text("metadata"),
  created_at: text("created_at"),
});

// Active user sessions — small dataset, no indexes.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
});

// Streak protector consumable. Tiny dataset per user queried on every streak render.
export const streak_protector = sqliteTable(
  "streak_protector",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    habit_id: text("habit_id"),
    date: text("date"),
    source: text("source"),
    granted_at: text("granted_at"),
    applied_at: text("applied_at"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** WHERE user_id = ? in getStreakProtectorsQuery */
    index("streak_protector_by_user").on(table.user_id),
    /** WHERE habit_id = ? when resolving protected days of a habit */
    index("streak_protector_by_habit").on(table.habit_id),
  ],
);

// Small global dataset (~20 themes) — indexes not needed.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// Single row per user — indexes not needed.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
});

export const drizzleSchema = {
  attachment,
  font_family,
  font_variant,
  habit,
  habit_completion,
  habit_reminder,
  habit_version,
  member,
  organization,
  session,
  streak_protector,
  themes,
  user,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "attachment",
  "habit",
  "habit_completion",
  "habit_reminder",
  "streak_protector",
] as const;

export type Attachment = InferSelectModel<typeof attachment>;
export type AttachmentInsert = InferInsertModel<typeof attachment>;
export type AttachmentRecord = Attachment;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type Habit = InferSelectModel<typeof habit>;
export type HabitInsert = InferInsertModel<typeof habit>;
export type HabitRecord = Habit;
export type HabitCompletion = InferSelectModel<typeof habit_completion>;
export type HabitCompletionInsert = InferInsertModel<typeof habit_completion>;
export type HabitCompletionRecord = HabitCompletion;
export type HabitReminder = InferSelectModel<typeof habit_reminder>;
export type HabitReminderInsert = InferInsertModel<typeof habit_reminder>;
export type HabitReminderRecord = HabitReminder;
export type HabitVersion = InferSelectModel<typeof habit_version>;
export type HabitVersionInsert = InferInsertModel<typeof habit_version>;
export type HabitVersionRecord = HabitVersion;
export type Member = InferSelectModel<typeof member>;
export type MemberInsert = InferInsertModel<typeof member>;
export type MemberRecord = Member;
export type Organization = InferSelectModel<typeof organization>;
export type OrganizationInsert = InferInsertModel<typeof organization>;
export type OrganizationRecord = Organization;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type StreakProtector = InferSelectModel<typeof streak_protector>;
export type StreakProtectorInsert = InferInsertModel<typeof streak_protector>;
export type StreakProtectorRecord = StreakProtector;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
